import React, { useMemo, useState } from "react"
import { Button } from "src/components/ui/button"
import { BlockPalette } from "./BlockPalette"
import { BlockList } from "./BlockList"
import { BlockInspector } from "./Inspectors"
import { makeBlock, newBlockId } from "./blockSchema"

// The drag-drop composer for the micro-site left panel.
//   content     = { version, blocks: [...], bg_color?: string }  (controlled)
//   onChange    = next content
//   images      = project's available images [{id,url,filename}]
//   project     = { launch_date, ... } for preview affordances
//   showBackground = whether to show the page-background colour control
//                    (off for listing pages, which use the brand ground)
export function MicrositeBuilder({ content, onChange, images = [], project, showBackground = true }) {
  const blocks = content?.blocks || []
  const bgColor = content?.bg_color || ""
  const [selectedId, setSelectedId] = useState(blocks[0]?.id || null)

  const imagesById = useMemo(
    () => Object.fromEntries(images.map((img) => [img.id, img])),
    [images]
  )
  const selectedBlock = blocks.find((b) => b.id === selectedId) || null

  const setBlocks = (next) => onChange({ ...content, blocks: next })
  const setBgColor = (val) => onChange({ ...content, bg_color: val || undefined })

  function addBlock(type) {
    const block = makeBlock(type)
    if (!block) return
    setBlocks([...blocks, block])
    setSelectedId(block.id)
  }

  function duplicateBlock(id) {
    const idx = blocks.findIndex((b) => b.id === id)
    if (idx < 0) return
    const copy = { ...blocks[idx], id: newBlockId(), props: { ...blocks[idx].props } }
    const next = [...blocks.slice(0, idx + 1), copy, ...blocks.slice(idx + 1)]
    setBlocks(next)
    setSelectedId(copy.id)
  }

  function deleteBlock(id) {
    const next = blocks.filter((b) => b.id !== id)
    setBlocks(next)
    if (selectedId === id) setSelectedId(next[0]?.id || null)
  }

  function updateSelectedProps(nextProps) {
    setBlocks(blocks.map((b) => (b.id === selectedId ? { ...b, props: nextProps } : b)))
  }

  return (
    <div className="space-y-4">
      {/* Page background color */}
      {showBackground && (
        <div className="flex items-center gap-3">
          <label className="text-[12px] font-medium text-slate-500 whitespace-nowrap">
            Page background
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={bgColor || "#f8fafc"}
              onChange={(e) => setBgColor(e.target.value)}
              className="h-7 w-10 cursor-pointer rounded border border-slate-200 p-0.5"
              title="Background colour"
            />
            {bgColor && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setBgColor("")}
                className="h-auto p-0 text-[11px] font-normal text-slate-400 hover:bg-transparent hover:text-slate-600"
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      )}

      <div>
        <p className="mb-1.5 text-[12px] font-medium text-slate-500">Add block</p>
        <BlockPalette onAdd={addBlock} />
      </div>

      <div className="grid grid-cols-[1fr_280px] gap-4">
        {/* Canvas */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <BlockList
            blocks={blocks}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onReorder={setBlocks}
            onDuplicate={duplicateBlock}
            onDelete={deleteBlock}
            imagesById={imagesById}
            project={project}
          />
        </div>

        {/* Inspector */}
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-slate-400">
            {selectedBlock ? "Block settings" : "Settings"}
          </p>
          <BlockInspector block={selectedBlock} images={images} onChange={updateSelectedProps} />
        </div>
      </div>
    </div>
  )
}
